import { useQuery } from '@tanstack/react-query';

import { useApiClient } from '@@common';

import {
	CodeBaseModel,
	CodeStoreFileModel,
	ContextModel,
	DeepOperationModel,
	DomainModelingDataModel,
	OperationExecutionTypeModel,
} from '../../../api/api';
import { ApiClientBase } from '../../../api/api';
import { IApiConfig } from '../../../api/ApiConfig';
import { contextDesignerQueryKeys } from '../ContextDesignerQueryKeys';
import { ContextArtifactLayoutModel } from './useContextArtifactLayout';

export interface ContextDesignerSnapshotModel {
	context: ContextModel;
	artifactLayout: ContextArtifactLayoutModel;
	domainModel: DomainModelingDataModel;
	operations: DeepOperationModel[];
	operationExecutionTypes: OperationExecutionTypeModel[];
	code?: ContextDesignerCodeSnapshotModel | null;
}

export interface ContextDesignerCodeSnapshotModel {
	domainFiles: CodeStoreFileModel[];
	applicationFiles: CodeStoreFileModel[];
	testFiles: CodeStoreFileModel[];
	domainCodeBase?: CodeBaseModel | null;
	applicationCodeBase?: CodeBaseModel | null;
	testCodeBase?: CodeBaseModel | null;
}

class ContextDesignerSnapshotClient extends ApiClientBase {
	private readonly http: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> };
	private readonly baseUrl: string;

	constructor(configuration: IApiConfig, baseUrl?: string, http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }) {
		super(configuration);
		this.http = http ?? (window as any);
		this.baseUrl = baseUrl ?? '';
	}

	async getDesignerSnapshot(contextId: string, includeCode: boolean): Promise<ContextDesignerSnapshotModel> {
		const response = await this.http.fetch(
			`${this.baseUrl}/Context/${encodeURIComponent(contextId)}/designer-snapshot?includeCode=${includeCode}`,
			await this.transformOptions({
				method: 'GET',
				headers: { Accept: 'application/json' },
			}),
		);

		if (!response.ok) {
			throw new Error(await response.text());
		}

		return (await response.json()) as ContextDesignerSnapshotModel;
	}
}

export function useContextDesignerSnapshot(contextId: string, includeCode: boolean) {
	const client = useApiClient(ContextDesignerSnapshotClient);

	return useQuery({
		queryKey: contextDesignerQueryKeys.designerSnapshot(contextId, includeCode),
		queryFn: () => client.getDesignerSnapshot(contextId, includeCode),
		enabled: !!contextId,
		staleTime: 10000,
		gcTime: 2 * 60 * 1000,
		refetchOnWindowFocus: false,
	});
}
